import { z } from 'zod'
import { actorCodec } from './actor'
import { deviceNameSchema } from '#model/entities/session'
import { telegramUserIdSchema } from '#model/entities/actor'

export const LOGIN_COOKIE = '__Host-molvia_login'
export const LOGIN_HEADER = 'X-Molvia-Login'
export const LOGIN_LIFETIME_SECONDS = 5 * 60
export const LOGIN_WINDOW_SECONDS = 60
export const LOGIN_WINDOW_LIMIT = 30

/**
 * What the internal channel's secret looks like: 32 random bytes in base64url (MOL-54, Р-5).
 * It is not the Telegram token, and it is generated once per copy by `bin/init-env.sh`.
 *
 * Here rather than three times over, and the third copy is what forced it (MOL-55, З-3): the
 * API's configuration, the client that carries the secret and the bot's own environment each
 * spelled the rule out for themselves. That is the shape `INVISIBLE`, `resource.ts` and
 * `secret.ts` were all in before a copy disagreed with its twin — and each of those cost a 500
 * to find out. Nothing about it belongs to one side of the wire, so it belongs to the contract.
 */
export const botApiSecretSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/)

const dateCodec = z.codec(z.iso.datetime(), z.date(), {
  decode: (value) => new Date(value),
  encode: (value) => value.toISOString(),
})

/**
 * The query of a login's start (MOL-68): `again=1` when this device began a login before and has
 * not come in since, so the funnel counts «Начать заново» as the same person rather than a new
 * one. The device's word, and all it can change is our own count — it opens nothing.
 */
export const loginStartQuerySchema = z.strictObject({ again: z.literal('1').optional() })

export const loginStartedCodec = z.strictObject({
  id: z.uuid(),
  url: z.url({ protocol: /^https$/ }).refine((value) => new URL(value).hostname === 't.me'),
  expiresAt: dateCodec,
})
export type LoginStarted = z.infer<typeof loginStartedCodec>

export const loginPollCodec = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('pending'), expiresAt: dateCodec }),
  z.strictObject({ status: z.literal('authenticated'), actor: actorCodec }),
])
export type LoginPoll = z.infer<typeof loginPollCodec>

export const loginPreviewCodec = z.strictObject({
  deviceName: deviceNameSchema.nullable(),
  createdAt: dateCodec,
  expiresAt: dateCodec,
  /**
   * Whether somebody has already said «yes» to this request — **not who** (Р-11: the Telegram
   * id does not leave the server).
   *
   * It exists because of what a lost answer used to look like (MOL-55, О-2): the API had
   * written the confirmation, the reply never arrived, and the bot told the person it had not
   * worked. Doing as told — opening the link again — then met «this link no longer works»,
   * while the browser was collecting the session. Both sentences were false. With this, the
   * bot can say the one true thing: it is confirmed, go back to the app.
   */
  confirmed: z.boolean(),
})
export type LoginPreview = z.infer<typeof loginPreviewCodec>

export const confirmLoginSchema = z.strictObject({ telegramUserId: telegramUserIdSchema })

/**
 * «Erase me», sent by the bot for the person who pressed the button (MOL-58). The id is the one
 * Telegram vouched for in that update — the channel's secret is what stops anyone else from
 * naming one — and a repeat is as good as the first: whoever had nothing left gets the same 204.
 */
export const eraseMeSchema = z.strictObject({ telegramUserId: telegramUserIdSchema })

/**
 * The most sessions `GET /sessions` lists (MOL-57). Every selection has a limit (MOL-7), and
 * one login is one row, so a real account holds a handful; `total` beside the rows lets a list
 * past the limit say that it is one rather than pass for whole.
 */
export const SESSIONS_LIMIT = 50

/**
 * One way into the account, as its owner sees it in «Устройства» (MOL-57).
 *
 * The token is not here and neither is its hash, and `expiresAt` is left out too: the list holds
 * only live sessions, and the date the term would run out moves with every visit, so printing it
 * would say something that stops being true tomorrow. `current` is the row this very request came
 * with — the screen cannot work that out, because the id of its own session never reaches it.
 */
export const sessionViewCodec = z.strictObject({
  id: z.uuid(),
  deviceName: deviceNameSchema.nullable(),
  createdAt: dateCodec,
  lastSeenAt: dateCodec,
  current: z.boolean(),
})
export type SessionView = z.infer<typeof sessionViewCodec>

export const sessionsResponseCodec = z
  .strictObject({
    sessions: z.array(sessionViewCodec).max(SESSIONS_LIMIT),
    total: z.number().int().nonnegative(),
  })
  // A request comes with one session, so at most one row can be it; and a list cut by the limit
  // never holds more rows than there are.
  .refine(({ sessions }) => sessions.filter((session) => session.current).length <= 1)
  .refine(({ sessions, total }) => total >= sessions.length)
export type SessionsResponse = z.infer<typeof sessionsResponseCodec>
